import { useEffect, useMemo, useState } from 'react';

/**
 * Comet-tail highlight engine for the bimodal reader.
 *
 * Given the current audio word, its chapter, playback time, and the user's
 * Eye-Voice Span preference (leadLetters), computes:
 *   - readWordIdx        — chapter-local index of the "read word" (d=0)
 *   - colorForWord(idx)  — the CSS color string for any word in the chapter
 *   - wordTransition     — CSS transition to apply to every word span
 *
 * The read point is `leadLetters` ahead of the audio letter, capped at
 * paragraph boundaries, sentence-ending punctuation, and long dramatic
 * silences. Inside an anticipation window before any cap lifts, the read
 * point pre-advances so the full lead is re-established the moment audio
 * resumes past the boundary. Short-word transitions get an extra word
 * appended so thin swatches ("a" / "I" / "to") don't visually collapse.
 *
 * Every constant in this file is a variable we've already had to tune once.
 * If these move into a user-facing Settings panel, this hook is the import
 * site — expose the constants through a preferences context and the logic
 * stays unchanged.
 */

// Zone colors. Let d = wordIndex - readWordIdx.
//   d <= -3  default text color
//   d = -2   deep blue
//   d = -1   sage with a hint of blue
//   d =  0   pure sage — the read word, where the eye should be
//   d = +1   sage with a hint of gold
//   d = +2   gold / khaki
//   d >= +3  default text color
const DEEP_BLUE = 'rgb(85, 125, 180)';
const SAGE_HINT_BLUE = 'rgb(149, 197, 108)';
const SAGE = 'rgb(160, 210, 95)';
const SAGE_HINT_GOLD = 'rgb(163, 203, 94)';
const GOLD = 'rgb(180, 160, 85)';

// CSS color transition. 150ms is long enough that the tail still "glides"
// rather than snapping word-by-word, but short enough that colors actually
// settle at their target before the next word shift (typical word stays at
// d=0 for ~300-400ms at normal reading speed). `linear` gives constant-rate
// color change — avoids the front-loaded feel of `ease-out` where most of
// the change happens in the first 60ms and then slowly approaches its target.
export const WORD_COLOR_TRANSITION = 'color 150ms linear';

// Cap triggers:
//   - Paragraph boundary (structural, always)
//   - Sentence end (structural, always)
//   - Long in-paragraph silence (audio-timing-based, > threshold)
const SILENCE_THRESHOLD_SEC = 0.5;
// Words ending with `.`, `!`, `?` (with optional closing quote/paren) cap
// like paragraph breaks. Over-triggers slightly on abbreviations like "Mr.";
// the visible cost is a brief pause, cheaper than maintaining exception lists.
// Used only as a fallback for manifests built before endsSentence was stamped.
const SENTENCE_END_PATTERN = /[.!?]["')\]]?$/;
// Before a cap lifts (audio about to resume past the boundary), expand the
// read point back toward the full lead so the EVS doesn't snap on resume.
const ANTICIPATION_WINDOW_SEC = 0.3;

/**
 * @param {Object} params
 * @param {number} params.chapterLocalWordIndex - -1 if no audio word yet
 * @param {Array}  params.currentChapterWords   - chapter's word array
 * @param {number} params.currentTime           - global audio time, seconds
 * @param {number} params.leadLetters           - user's EVS preference
 */
export function useCometTail({
  chapterLocalWordIndex,
  currentChapterWords,
  currentTime,
  leadLetters,
}) {
  // Respect the OS-level reduced-motion preference (WCAG 2.2.2). The colors
  // still change as the read point advances, but the gradient transition is
  // disabled so users sensitive to motion don't see a continuous color flow.
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(
    () => typeof window !== 'undefined'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (e) => setPrefersReducedMotion(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Per-letter audio position (chapter-local). Each letter of the current
  // audio word is assumed to take (wordDuration / wordLength) so the letter
  // position advances smoothly as the word is spoken.
  const audioCharPos = useMemo(() => {
    if (chapterLocalWordIndex < 0 || chapterLocalWordIndex >= currentChapterWords.length) return 0;
    const word = currentChapterWords[chapterLocalWordIndex];
    if (!word) return 0;
    const wordDuration = word.end - word.start;
    const timeIntoWord = currentTime - word.start;
    const progress = Math.max(0, Math.min(1, timeIntoWord / wordDuration));
    let charOffset = 0;
    for (let i = 0; i < chapterLocalWordIndex; i++) {
      charOffset += currentChapterWords[i].text.length + 1;
    }
    return charOffset + (progress * word.text.length);
  }, [chapterLocalWordIndex, currentTime, currentChapterWords]);

  // Read point: `leadLetters` ahead of audio, capped at the next semantic or
  // audio boundary. Anticipation window pre-expands before resume.
  const readPoint = useMemo(() => {
    const defaultPoint = audioCharPos + leadLetters;
    if (currentChapterWords.length === 0 || chapterLocalWordIndex < 0) {
      return defaultPoint;
    }

    let walkOffset = 0;
    for (let i = 0; i < chapterLocalWordIndex; i++) {
      walkOffset += currentChapterWords[i].text.length + 1;
    }
    for (let i = chapterLocalWordIndex; i < currentChapterWords.length - 1; i++) {
      const word = currentChapterWords[i];
      const wordEndLetter = walkOffset + word.text.length;
      if (wordEndLetter > defaultPoint) break;

      const nextWord = currentChapterWords[i + 1];
      // Prefer pre-computed boundary flags when the manifest has them
      // (stamped at generation time). Fall back to inline computation for
      // books generated before the boundary-flag rollout.
      const isParagraphBreak = word.endsParagraph !== undefined
        ? word.endsParagraph
        : word.paragraphIndex !== nextWord.paragraphIndex;
      const isSentenceEnd = word.endsSentence !== undefined
        ? word.endsSentence
        : SENTENCE_END_PATTERN.test(word.text);
      const silenceGapSec = word.silenceAfterMs !== undefined
        ? word.silenceAfterMs / 1000
        : nextWord.start - word.end;
      const isLongSilence = silenceGapSec > SILENCE_THRESHOLD_SEC;

      if (isParagraphBreak || isSentenceEnd || isLongSilence) {
        const timeUntilResume = nextWord.start - currentTime;
        if (timeUntilResume > ANTICIPATION_WINDOW_SEC) return wordEndLetter;
        if (timeUntilResume > 0) {
          const progress = 1 - (timeUntilResume / ANTICIPATION_WINDOW_SEC);
          return wordEndLetter + progress * leadLetters;
        }
        // Cap lifted — fall through to default.
      }
      walkOffset = wordEndLetter + 1;
    }
    return defaultPoint;
  }, [audioCharPos, leadLetters, currentTime, currentChapterWords, chapterLocalWordIndex]);

  // Clamp: read point never falls behind audio. Can happen at seams where a
  // just-passed silence is still the cap before anticipation kicks in; we'd
  // rather the lead shrink to 0 than invert.
  const clampedReadPoint = Math.max(readPoint, audioCharPos);

  // Read word — the word containing the clamped read point. Attribute the
  // trailing space to the preceding word so fractional positions in the
  // between-words gap don't fall through to the chapter-end fallback.
  const readWordIdx = useMemo(() => {
    if (currentChapterWords.length === 0) return -1;
    let walkOffset = 0;
    for (let i = 0; i < currentChapterWords.length; i++) {
      const nextWalkOffset = walkOffset + currentChapterWords[i].text.length + 1;
      if (clampedReadPoint < nextWalkOffset) return i;
      walkOffset = nextWalkOffset;
    }
    return currentChapterWords.length - 1;
  }, [clampedReadPoint, currentChapterWords]);

  // Short-word zone extension. When the d=±1 transition word is < 3 letters
  // the thin swatch looks visually flimsy; extend the same color one more
  // word in that direction and push the pure anchor one further out.
  const colorForWord = useMemo(() => {
    const nextWord = currentChapterWords[readWordIdx + 1];
    const prevWord = readWordIdx > 0 ? currentChapterWords[readWordIdx - 1] : null;
    const extendAhead = !!nextWord && nextWord.text.length < 3;
    const extendBehind = !!prevWord && prevWord.text.length < 3;

    return (wordIdx) => {
      const d = wordIdx - readWordIdx;
      if (d === 0) return SAGE;
      if (d === 1) return SAGE_HINT_GOLD;
      if (d === 2) return extendAhead ? SAGE_HINT_GOLD : GOLD;
      if (d === 3 && extendAhead) return GOLD;
      if (d === -1) return SAGE_HINT_BLUE;
      if (d === -2) return extendBehind ? SAGE_HINT_BLUE : DEEP_BLUE;
      if (d === -3 && extendBehind) return DEEP_BLUE;
      return 'inherit';
    };
  }, [readWordIdx, currentChapterWords]);

  return {
    readWordIdx,
    colorForWord,
    wordTransition: prefersReducedMotion ? 'none' : WORD_COLOR_TRANSITION,
  };
}
